import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react'
import { authReducer } from './auth/authReducer'
import { AUTH_STATES, AUTH_ACTIONS, initialState } from './auth/authTypes'
import { setupAxiosInterceptors } from '../utils/axiosConfig'
import { useAuthActions } from '../hooks/useAuthActions'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import AuthRequired from '../components/auth/AuthRequired'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  const authActions = useAuthActions(dispatch)

  // Configure axios interceptors
  useEffect(() => {
    const cleanup = setupAxiosInterceptors(dispatch, AUTH_ACTIONS)
    return cleanup
  }, [])

  // Check authentication status on mount
  useEffect(() => {
    authActions.checkAuthStatus()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Memoized context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    ...state,

    // Actions
    ...authActions,

    // Helpers
    isLoading: state.status === AUTH_STATES.LOADING,
    isError: state.status === AUTH_STATES.ERROR
  }), [state, authActions])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export const withAuth = (Component) => {
  return (props) => {
    const auth = useAuth()

    if (auth.isLoading) {
      return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner text="Verifying authentication..." /></div>
    }

    if (!auth.isAuthenticated) {
      return <AuthRequired />
    }

    return <Component {...props} />
  }
}

export default AuthContext